import React from 'react';

import FeaturedImage, { FeaturedImageProps } from './FeaturedImage';

import {
  GridButton,
  GridItemWrapper,
  GridRow,
  GridTextWrapper,
  GridWrapper,
  StyledHTitleMedium4,
  StyledPBodyBase,
  VideoWrapper,
  Video,
} from './GridTwoByTwo.styles';

import type { ButtonV3Props } from '../ButtonV3/ButtonV3';

type FeaturedVideoProps = {
  src: string;
};

type BaseGridItemProps = {
  copy: { body: string | JSX.Element; header: string };
  href: string;
  onClick?: () => void;
  linkText: string;
  buttonPropOverrides?: ButtonV3Props;
};

export type GridTwoByTwoItemProps =
  | (BaseGridItemProps & {
      isAssetVideo: true;
      video: FeaturedVideoProps;
    })
  | (BaseGridItemProps & {
      isAssetVideo?: false;
      image: FeaturedImageProps;
    });

export type GridTwoByTwoProps = {
  gridItems: GridTwoByTwoItemProps[];
  title?: React.ReactNode;
};

/**
 * Grid that maintains a two column layout for screens larger than
 * mobile. The first row starts with an asset (img or video) on the
 * left and some text (header, body, cta) on the right-hand side.
 * The second row alternates the two grids, and so forth.
 */

export const GridTwoByTwo: React.FC<GridTwoByTwoProps> = ({ gridItems, title }): JSX.Element => {
  const renderGridItem = (item: GridTwoByTwoItemProps, i: number) => {
    const {
      copy: { header, body },
      href,
      onClick,
      isAssetVideo,
      linkText,
      buttonPropOverrides,
    } = item;

    return (
      <GridRow key={i}>
        <GridItemWrapper key={i}>
          {isAssetVideo ? (
            <VideoWrapper>
              <Video autoPlay muted loop playsInline>
                <source src={item.video.src} type="video/mp4" />
              </Video>
            </VideoWrapper>
          ) : (
            <FeaturedImage {...item.image} />
          )}
        </GridItemWrapper>
        <GridTextWrapper>
          <StyledHTitleMedium4 presentation="h3">{header}</StyledHTitleMedium4>
          <StyledPBodyBase>{body}</StyledPBodyBase>
          <GridButton
            component="a"
            href={href}
            onClick={onClick}
            mobileSize="large"
            tabletSize="large"
            {...(buttonPropOverrides || {})}
          >
            {linkText}
          </GridButton>
        </GridTextWrapper>
      </GridRow>
    );
  };

  return (
    <section>
      {title || null}
      <GridWrapper>{gridItems?.map(renderGridItem)}</GridWrapper>
    </section>
  );
};

export default GridTwoByTwo;
